/**
 * Fabric.js v7 patches for Textbox:
 *
 * 1. _renderTextDecoration — Fabric draws a negative-width fillRect on
 *    empty text lines when charSpacing > 0, producing a small dash
 *    artifact. Patch disables decoration on empty lines while preserving
 *    it on non-empty lines.
 *
 * 2. updateFromTextArea — anchors position to originX/originY so the
 *    layer doesn't drift when width changes (e.g. all text deleted).
 */
import { Textbox } from 'fabric';
import type { TextDecorationStyleMap, TextDecorationStyles } from '@/types/canvas/textDecoration';

// ── Helpers ─────────────────────────────────────────────────────────

/**
 * Textbox wraps text, so visual line indices (_textLines) differ from
 * logical line indices (styles). _styleMap bridges the two.
 */
function resolveLogicalIndex(
  styleMap: TextDecorationStyleMap,
  visualLine: number,
  visualChar: number
) {
  const map = styleMap[visualLine];
  if (!map) return null;
  return { line: map.line, char: map.offset + visualChar };
}

function ensureStyleSlot(styles: TextDecorationStyles, line: number, char: number) {
  if (!styles[line]) styles[line] = {};
  if (!styles[line]![char]) styles[line]![char] = {};
  return styles[line]![char]!;
}

function deleteStyleSlot(styles: TextDecorationStyles, line: number, char: number) {
  delete styles[line]![char];
  if (Object.keys(styles[line]!).length === 0) delete styles[line];
}

// ── Patch 1: skip decoration on empty lines ─────────────────────────

const originalRender = Textbox.prototype._renderTextDecoration;

Textbox.prototype._renderTextDecoration = function (
  this: Textbox,
  ctx: CanvasRenderingContext2D,
  type: 'underline' | 'linethrough' | 'overline'
) {
  if (!this[type] && !this.styleHas(type, undefined as unknown as number)) {
    return;
  }

  const emptyVisualLines: number[] = [];
  for (let i = 0; i < this._textLines.length; i++) {
    if (this._textLines[i]!.length === 0) emptyVisualLines.push(i);
  }

  if (emptyVisualLines.length === 0) {
    originalRender.call(this, ctx, type);
    return;
  }

  // --- Temporarily mutate styles, render, then restore ---

  const baseFlag = this[type];
  const styleMap = (this as unknown as { _styleMap: TextDecorationStyleMap })._styleMap;
  const styles = this.styles as TextDecorationStyles;
  const undoOps: (() => void)[] = [];

  // 1) Mark empty lines as decoration=false so Fabric skips them.
  for (const vi of emptyVisualLines) {
    const resolved = resolveLogicalIndex(styleMap, vi, 0);
    if (!resolved) continue;
    const { line, char } = resolved;

    const prev = styles[line]?.[char];
    const slot = ensureStyleSlot(styles, line, char);
    slot[type] = false;

    undoOps.push(() => {
      if (prev === undefined) deleteStyleSlot(styles, line, char);
      else styles[line]![char] = prev;
    });
  }

  // 2) We must set the base flag to false (Fabric reads it for empty
  //    lines). But that would also hide decoration on non-empty lines
  //    that rely on it. So promote the base flag into explicit per-char
  //    styles on every non-empty character that doesn't already have one.
  if (baseFlag) {
    for (let i = 0; i < this._textLines.length; i++) {
      const lineChars = this._textLines[i]!;
      if (lineChars.length === 0) continue;

      const resolved = resolveLogicalIndex(styleMap, i, 0);
      if (!resolved) continue;
      const logLine = resolved.line;
      const baseOffset = resolved.char;

      for (let j = 0; j < lineChars.length; j++) {
        const logChar = baseOffset + j;
        if (styles[logLine]?.[logChar]?.[type] !== undefined) continue;

        const slot = ensureStyleSlot(styles, logLine, logChar);
        slot[type] = true;

        undoOps.push(() => {
          delete styles[logLine]![logChar]![type];
          if (Object.keys(styles[logLine]![logChar]!).length === 0) {
            deleteStyleSlot(styles, logLine, logChar);
          }
        });
      }
    }
  }

  // 3) Disable base flag and render.
  this[type] = false;
  try {
    originalRender.call(this, ctx, type);
  } finally {
    this[type] = baseFlag;
    for (let i = undoOps.length - 1; i >= 0; i--) undoOps[i]!();
  }
};

// ── Patch 2: updateFromTextArea position anchor ─────────────────────

const originalUpdate = Textbox.prototype.updateFromTextArea;

Textbox.prototype.updateFromTextArea = function (this: Textbox) {
  const textarea = (
    this as unknown as { hiddenTextarea: HTMLTextAreaElement | null }
  ).hiddenTextarea;
  if (!textarea) return;

  const anchor = this.getPositionByOrigin(this.originX, this.originY);
  originalUpdate.call(this);
  this.setPositionByOrigin(anchor, this.originX, this.originY);
  this.setCoords();
};
